<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';

import type {
  FrameworkDisplay,
  FrameworkListDisplay,
  PlatformFrameworkVersionOverview,
} from '@/clients/v1';
import type {
  Framework,
  FrameworkIcon,
  PlatformFramework,
  PlatformFrameworkOverview,
} from '@/clients/v2';

const props = defineProps({
  framework: {
    type: Object as PropType<
      | PlatformFrameworkVersionOverview
      | PlatformFrameworkOverview
      | Framework
      | FrameworkDisplay
      | FrameworkListDisplay
      | PlatformFramework
      | FrameworkIcon
    >,

    required: true,
  },

  tooltip: {
    type: Boolean,
    default: true,
  },

  dense: {
    type: Boolean,
    default: true,
  },

  size: {
    type: [Number, String] as PropType<number | string>,
    default: undefined,
  },
});

const frameworkImage = ref<string | null>(null);

const sizeStyle = computed(() => {
  if (props.size === undefined) return undefined;
  const value = typeof props.size === 'number' ? `${props.size}px` : props.size;
  return { width: value, height: value };
});

const outdatedFramework = computed(() => {
  return (
    'is_framework_latest_version' in props.framework && !props.framework.is_framework_latest_version
  );
});

watch(
  () => props.framework,
  (newV) => {
    if (newV && props.framework.icon_type === 'image' && props.framework.icon_base64) {
      frameworkImage.value = `data:image/svg+xml;base64,${props.framework.icon_base64}`;
    } else {
      frameworkImage.value = null;
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="icon-wrapp relative-position">
    <span
      v-if="framework.icon_type === 'flag' && framework.icon"
      class="flag fi"
      :class="`fi-${framework.icon}`"
      :style="sizeStyle"
    />
    <div v-if="framework.icon_type === 'image' && frameworkImage" class="img__container">
      <img :src="frameworkImage" :class="dense ? 'img-dense' : ''" :style="sizeStyle" alt="" />
    </div>

    <q-icon
      v-if="outdatedFramework"
      name="o_schedule"
      :size="dense ? '16px' : '20px'"
      class="outdated bg-white text-muted"
      :class="dense ? 'outdated-dense' : ''"
    />

    <q-tooltip v-if="tooltip" anchor="top middle" self="center middle" max-width="200px">
      {{ `${framework.name}${outdatedFramework ? '(outdated)' : ''}` }}
    </q-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.icon-wrapp {
  .img__container {
    width: fit-content;
  }
  img {
    width: 42px;
    height: 42px;
    display: block;
    border-radius: 50%;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  }

  .img-dense {
    width: 32px;
    height: 32px;
  }

  :deep(img) {
    border-radius: 50%;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  }

  .flag {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
    background-size: cover;
  }

  .outdated {
    position: absolute;
    top: -4px;
    right: -6px;
    border-radius: 50%;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  }
  .outdated-dense {
    top: -2px;
    right: -4px;
  }
}
</style>
